import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { useMemo } from "react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import { useGetConnectedAccounts } from "../../api/hooks";
import { AccountsTableToolbar } from "./accounts-table-toolbar";
import { getConnectedAccountsColumns } from "./columns";

export const ConnectedAccounts = ({
  oauthProviderId,
}: {
  oauthProviderId: string;
}) => {
  const { canProject } = useProjectPermissions();

  // The provider detail route only requires auth_providers_read, but the
  // connected-users list needs user_connections_read. Skip the section (and its
  // suspense fetch) when the caller can't read connections, so the request never
  // 403s on a page they're otherwise allowed to see.
  if (!canProject(PROJECT.userConnections.read)) {
    return null;
  }

  return <ConnectedAccountsCard oauthProviderId={oauthProviderId} />;
};

const ConnectedAccountsCard = ({
  oauthProviderId,
}: {
  oauthProviderId: string;
}) => {
  const { data: connectedAccounts = [] } = useGetConnectedAccounts(
    "project",
    oauthProviderId
  );
  const { canProject } = useProjectPermissions();
  const columns = useMemo(
    () =>
      getConnectedAccountsColumns(canProject(PROJECT.userConnections.delete)),
    [canProject]
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 font-semibold text-lg">
          Connected users
        </CardTitle>
        <CardDescription className="text-sm">
          View and manage connected users
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          className="space-y-4"
          columns={columns}
          data={connectedAccounts}
        >
          <AccountsTableToolbar />
          <DataTableContent stickyColumnBg="card" />
          <DataTablePagination />
        </DataTable>
      </CardContent>
    </Card>
  );
};
